import React, { useState, useMemo } from "react";
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";

import TicketForm from "./TicketForm";
import type { Ticket } from "./types";
import "./Ticket.css";

import Modal from "./components/Modal";
import TicketsListPanel from "./components/TicketsListPanel";
import TargetsPanel from "./components/TargetsPanel";
import TicketDraggableCard from "./components/TicketDraggableCard";
import { useTicketsBoard } from "./hooks/useTicketsBoard";
import ToastHost from "./components/ToastHost";
import { useToasts } from "./hooks/useToasts";
import { useConfirm } from "./hooks/useConfirm";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billlede-1.png";
import { getAuthHeaders } from "../../../api/authHelper";

import { canAssignTicketToShift, getShiftTicketMismatchReason } from "./utils/ticketRules";

const TicketsBoardPage: React.FC = () => {
    const toast = useToasts();
    const { confirm, ConfirmUI } = useConfirm();
    const apiUrl = process.env.REACT_APP_API_URL;
    const webUrl = process.env.REACT_APP_WEB_URL;

    const {
        filteredTickets,
        selectedTicket,
        setSelectedTicket,
        ticketSearch,
        setTicketSearch,
        showActiveOnly,
        setShowActiveOnly,
        hideExpired,
        setHideExpired,

        tab,
        setTab,

        personSearch,
        setPersonSearch,
        personChipsById,

        events,
        eventsLoading,
        selectedEventId,
        setSelectedEventId,
        shifts,
        shiftsLoading,
        refreshShifts,
        shiftChipsById,

        functionsLoading,
        functionsError,
        filteredFunctions,
        functionSearch,
        setFunctionSearch,
        functionChipsById,
        refreshFunctions,

        onCreateTicket,
        onSaveTicket,
        onDeleteTicket,
        assignTicketTo,
    } = useTicketsBoard({ confirm, toast });

    const [activeDragTicket, setActiveDragTicket] = useState<Ticket | null>(null);
    const [ignoreShiftTimeRule, setIgnoreShiftTimeRule] = useState(false);
    const [overviewOpen, setOverviewOpen] = useState(false);
    const [overviewData, setOverviewData] = useState<any[]>([]);
    const [loadingOverview, setLoadingOverview] = useState(false);
    const [overviewFilters, setOverviewFilters] = useState({
        ticket: "",
        date: "",
        time: "",
    });

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 5 },
        })
    );

    const onDragStart = (event: any) => {
        const ticket: Ticket | undefined = event?.active?.data?.current?.ticket;
        setActiveDragTicket(ticket ?? null);
    };

    const onDragEnd = async (event: DragEndEvent) => {
        const ticket: Ticket | undefined = event.active.data.current?.ticket;
        setActiveDragTicket(null);

        if (!ticket?.ticketID) return;
        if (!event.over?.id) return;

        const overId = String(event.over.id);

        if (overId.startsWith("person-")) {
            const personID = Number(overId.replace("person-", ""));
            if (!Number.isFinite(personID) || personID <= 0) return;

            await assignTicketTo(ticket.ticketID, "person", personID);
            return;
        }

        if (overId.startsWith("shift-")) {
            const shiftID = Number(overId.replace("shift-", ""));
            if (!Number.isFinite(shiftID) || shiftID <= 0) return;

            const shift = shifts.find((s) => s.shiftsID === shiftID);
            if (!shift) return;

            if (!ignoreShiftTimeRule && !canAssignTicketToShift(ticket, shift)) {
                const reason =
                    getShiftTicketMismatchReason(ticket, shift) ??
                    "Ticket kan ikke tildeles denne vagt";

                toast.error(reason, "Ugyldig tildeling");
                return;
            }

            await assignTicketTo(ticket.ticketID, "shift", shiftID);
            return;
        }

        if (overId.startsWith("function-")) {
            const functionID = Number(overId.replace("function-", ""));
            if (!Number.isFinite(functionID) || functionID <= 0) return;

            await assignTicketTo(ticket.ticketID, "function", functionID);
        }
    };

    const onBulkCreateTickets = async (tickets: Ticket[]) => {
        for (const ticket of tickets) {
            await onSaveTicket(ticket);
        }

        toast.success(`${tickets.length} tickets oprettet`, "Bulk opret");
    };

    const loadOverview = async () => {
        setLoadingOverview(true);
        try {
            const apiUrl = process.env.REACT_APP_API_URL;

            const res = await fetch(`${apiUrl}/api/persons/ticket-overview`, {
                headers: {
                    "Content-Type": "application/json",
                    ...getAuthHeaders(),
                },
            });

            if (!res.ok) {
                const text = await res.text();
                console.error("Ticket overview API error:", res.status, text);
                throw new Error("Kunne ikke hente ticket overview");
            }

            const data = await res.json();
            setOverviewData(data);
            setOverviewOpen(true);
        } catch (err) {
            console.error(err);
            toast.error("Kunne ikke hente ticket overview", "Fejl");
        } finally {
            setLoadingOverview(false);
        }
    };

    const exportOverviewToCSV = () => {
        const rows = filteredOverviewData;

        const headers = ["Ticket", "Date", "Time", "Received", "Used"];

        const csv = [
            headers.join(","),
            ...rows.map((t) => {
                const ticket = (t.ticketDescription ?? `Ticket ${t.ticketID}`).replace(/,/g, " ");
                const date = t.ticketTimeStart
                    ? new Date(t.ticketTimeStart).toLocaleDateString()
                    : "-";

                const time =
                    t.ticketTimeStart && t.ticketTimeEnd
                        ? `${new Date(t.ticketTimeStart).toLocaleTimeString()} - ${new Date(
                            t.ticketTimeEnd
                        ).toLocaleTimeString()}`
                        : "-";

                return [
                    ticket,
                    date,
                    time,
                    t.receivedCount ?? 0,
                    t.usedCount ?? 0,
                ].join(",");
            }),
        ].join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "ticket-overview.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const filteredOverviewData = useMemo(() => {
        return overviewData.filter((t) => {
            const ticketText =
                (t.ticketDescription ?? `Ticket ${t.ticketID}`)
                    .toString()
                    .toLowerCase();

            const dateText = t.ticketTimeStart
                ? new Date(t.ticketTimeStart).toLocaleDateString().toLowerCase()
                : "";

            const timeText =
                t.ticketTimeStart && t.ticketTimeEnd
                    ? `${new Date(t.ticketTimeStart).toLocaleTimeString()} - ${new Date(
                        t.ticketTimeEnd
                    ).toLocaleTimeString()}`.toLowerCase()
                    : "";

            return (
                ticketText.includes(overviewFilters.ticket.toLowerCase()) &&
                dateText.includes(overviewFilters.date.toLowerCase()) &&
                timeText.includes(overviewFilters.time.toLowerCase())
            );
        });
    }, [overviewData, overviewFilters]);

    return (
        <div>
            <HeroSectionV2
                titleEn="Ticket"
                titleDa="Billetter"
                descriptionEn="Here you can create tickets and assign them"
                descriptionDa="Her kan du lave billetter og tildele dem"
                imageSrc={Billede1}
            />

            <div style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: 16 }}>
                <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
                    <TicketsListPanel
                        tickets={filteredTickets}
                        selectedTicket={selectedTicket}
                        onSelectTicket={setSelectedTicket}
                        onCreate={onCreateTicket}
                        onDelete={onDeleteTicket}
                        onBulkCreate={onBulkCreateTickets}
                        ticketSearch={ticketSearch}
                        setTicketSearch={setTicketSearch}
                        showActiveOnly={showActiveOnly}
                        setShowActiveOnly={setShowActiveOnly}
                        hideExpired={hideExpired}
                        setHideExpired={setHideExpired}
                        ignoreShiftTimeRule={ignoreShiftTimeRule}
                        setIgnoreShiftTimeRule={setIgnoreShiftTimeRule}
                    />

                    <TargetsPanel
                        tab={tab}
                        setTab={setTab}
                        activeTicket={activeDragTicket ?? selectedTicket ?? null}
                        ignoreShiftTimeRule={ignoreShiftTimeRule}
                        personSearch={personSearch}
                        setPersonSearch={setPersonSearch}
                        personChipsById={personChipsById}
                        events={events}
                        eventsLoading={eventsLoading}
                        selectedEventId={selectedEventId}
                        setSelectedEventId={setSelectedEventId}
                        shifts={shifts}
                        shiftsLoading={shiftsLoading}
                        refreshShifts={refreshShifts}
                        shiftChipsById={shiftChipsById}
                        functionsLoading={functionsLoading}
                        functionsError={functionsError}
                        refreshFunctions={refreshFunctions}
                        functionSearch={functionSearch}
                        setFunctionSearch={setFunctionSearch}
                        functions={filteredFunctions}
                        functionChipsById={functionChipsById}
                        onOpenOverview={loadOverview}
                    />

                    <DragOverlay>
                        {activeDragTicket ? (
                            <div style={{ width: 360 }}>
                                <TicketDraggableCard
                                    ticket={activeDragTicket}
                                    selected={false}
                                    onSelect={() => { }}
                                />
                            </div>
                        ) : null}
                    </DragOverlay>

                    <Modal
                        open={!!selectedTicket}
                        title={selectedTicket?.ticketID ? "Edit Ticket" : "Create Ticket"}
                        onClose={() => setSelectedTicket(null)}
                    >
                        {selectedTicket ? (
                            <TicketForm
                                ticket={selectedTicket}
                                onSave={onSaveTicket}
                                onCancel={() => setSelectedTicket(null)}
                            />
                        ) : null}
                    </Modal>

                    <Modal open={overviewOpen} onClose={() => setOverviewOpen(false)} title="Ticket overview">
                        <div style={{ maxHeight: 500, overflow: "auto" }}>
                            <table
                                style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontSize: "14px",
                                }}
                            >
                                <thead>
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "flex-start",
                                            marginBottom: 10,
                                        }}
                                    >
                                        <button
                                            onClick={exportOverviewToCSV}
                                            style={{
                                                padding: "6px 10px",
                                                borderRadius: 6,
                                                border: "1px solid #ccc",
                                                background: "#2e7d32",
                                                color: "white",
                                                cursor: "pointer",
                                                fontSize: 13,
                                            }}
                                        >
                                            Export visible rows
                                        </button>
                                    </div>
                                    <tr style={{ background: "#f5f5f5" }}>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                padding: 10,
                                                fontWeight: 600,
                                                borderBottom: "2px solid #ddd",
                                                position: "sticky",
                                                top: 0,
                                                background: "#f5f5f5",
                                            }}
                                        >
                                            Ticket
                                            <input
                                                value={overviewFilters.ticket}
                                                onChange={(e) =>
                                                    setOverviewFilters((f) => ({
                                                        ...f,
                                                        ticket: e.target.value,
                                                    }))
                                                }
                                                style={{
                                                    marginTop: 6,
                                                    width: "100%",
                                                    padding: "4px 6px",
                                                    fontSize: 12,
                                                    borderRadius: 4,
                                                    border: "1px solid #ccc",
                                                }}
                                            />
                                        </th>

                                        <th
                                            style={{
                                                textAlign: "left",
                                                padding: 10,
                                                fontWeight: 600,
                                                borderBottom: "2px solid #ddd",
                                                position: "sticky",
                                                top: 0,
                                                background: "#f5f5f5",
                                            }}
                                        >
                                            Date
                                            <input
                                                value={overviewFilters.date}
                                                onChange={(e) =>
                                                    setOverviewFilters((f) => ({
                                                        ...f,
                                                        date: e.target.value,
                                                    }))
                                                }
                                                style={{
                                                    marginTop: 6,
                                                    width: "100%",
                                                    padding: "4px 6px",
                                                    fontSize: 12,
                                                    borderRadius: 4,
                                                    border: "1px solid #ccc",
                                                }}
                                            />
                                        </th>

                                        <th
                                            style={{
                                                textAlign: "left",
                                                padding: 10,
                                                fontWeight: 600,
                                                borderBottom: "2px solid #ddd",
                                                position: "sticky",
                                                top: 0,
                                                background: "#f5f5f5",
                                            }}
                                        >
                                            Time
                                            <input
                                                value={overviewFilters.time}
                                                onChange={(e) =>
                                                    setOverviewFilters((f) => ({
                                                        ...f,
                                                        time: e.target.value,
                                                    }))
                                                }
                                                style={{
                                                    marginTop: 6,
                                                    width: "100%",
                                                    padding: "4px 6px",
                                                    fontSize: 12,
                                                    borderRadius: 4,
                                                    border: "1px solid #ccc",
                                                }}
                                            />
                                        </th>

                                        <th
                                            style={{
                                                textAlign: "left",
                                                padding: 10,
                                                fontWeight: 600,
                                                borderBottom: "2px solid #ddd",
                                                position: "sticky",
                                                top: 0,
                                                background: "#f5f5f5",
                                            }}
                                        >
                                            Received
                                        </th>

                                        <th
                                            style={{
                                                textAlign: "left",
                                                padding: 10,
                                                fontWeight: 600,
                                                borderBottom: "2px solid #ddd",
                                                position: "sticky",
                                                top: 0,
                                                background: "#f5f5f5",
                                            }}
                                        >
                                            Used
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {filteredOverviewData.map((t) => (
                                        <tr key={t.ticketID} style={{ borderBottom: "1px solid #eee" }}>
                                            <td style={{ padding: 10 }}>
                                                {t.ticketDescription ?? `Ticket ${t.ticketID}`}
                                            </td>

                                            <td style={{ padding: 10 }}>
                                                {t.ticketTimeStart
                                                    ? new Date(t.ticketTimeStart).toLocaleDateString()
                                                    : "-"}
                                            </td>

                                            <td style={{ padding: 10 }}>
                                                {t.ticketTimeStart && t.ticketTimeEnd
                                                    ? `${new Date(t.ticketTimeStart).toLocaleTimeString()} - ${new Date(
                                                        t.ticketTimeEnd
                                                    ).toLocaleTimeString()}`
                                                    : "-"}
                                            </td>

                                            <td style={{ padding: 10 }}>{t.receivedCount}</td>

                                            <td
                                                style={{
                                                    padding: 10,
                                                    fontWeight: 600,
                                                    color:
                                                        t.usedCount === 0
                                                            ? "#d32f2f"
                                                            : t.usedCount < t.receivedCount
                                                                ? "#f9a825"
                                                                : "#2e7d32",
                                                }}
                                            >
                                                {t.usedCount}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Modal>

                    {ConfirmUI}

                    <ToastHost items={toast.toasts} onDismiss={toast.dismiss} />
                </DndContext>
            </div>
        </div>
    );
};

export default TicketsBoardPage;